// src/components/StatusInstructions.js — /status-instructions, what each
// ticket status means for the AI agent. The agent alone sets a ticket's status
// with its own tools; these definitions are what it reads to choose. Per
// status: the default definition (sent verbatim to the AI), an optional
// account wording that replaces it, « L'IA ne pose jamais ce statut » (the
// team still sets it) and « L'IA ne fait plus rien sur ce statut » (always on
// for the resolved ones). Tiles count the saved state and filter (?filter=).
// API: GET/PUT /api/logistics/status-instructions
//   → {statuses: [{value, default_definition, custom_instruction, ai_settable,
//      ai_disabled, ai_inactive, ai_inactive_locked}], …}
//   PUT {instructions, disabled_statuses, inactive_statuses, carrier_scopes}.
//   carrier_scopes (KI-315): status → the carriers the AI uses it for (none =
//   every carrier); carrier_options: the account's carriers a scope can name.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Ban, ChevronRight, CirclePause, ListChecks, PenLine, RotateCcw, Save, X } from 'lucide-react';
import {
  Badge, Banner, Card, Field, Kpi, PageHeader, Switch, Toast,
} from '../design/ui';
import { getStatusLabel } from '../constants/ticketStatuses';
import {
  STATUS_FAMILY_ORDER, STATUS_FILTERS, authHeaders, buildStatusPayload, formatCount, matchesStatusFilter,
  statusFamily, statusFlags, statusFormFrom, statusSummary,
} from './aiAgentsShared';
import './StatusInstructions.css';

const firstLine = (text) => String(text || '').trim().split('\n')[0];

export { carrierScopesPayload } from './aiAgentsShared';

const StatusInstructions = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = STATUS_FILTERS.includes(searchParams.get('filter')) ? searchParams.get('filter') : 'all';

  const [statuses, setStatuses] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState({ instructions: {}, aiDisabled: {}, aiInactive: {}, carrierScopes: {} });
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const applyEntries = useCallback((entries, options) => {
    setStatuses(entries);
    setForm(statusFormFrom(entries));
    setCarrierOptions(Array.isArray(options) ? options : []);
  }, []);

  const fetchData = useCallback(async () => {
    setLoadError(null);
    try {
      const response = await axios.get('/api/logistics/status-instructions', { headers: authHeaders() });
      applyEntries((response.data && response.data.statuses) || [], response.data && response.data.carrier_options);
    } catch (error) {
      console.error('Failed to fetch status instructions:', error);
      setStatuses((previous) => previous || []);
      setLoadError(t('statusInstructions.loadError', 'Impossible de charger les instructions de statut.'));
    }
  }, [applyEntries, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const change = (key, status, value) => {
    setForm((previous) => ({ ...previous, [key]: { ...previous[key], [status]: value } }));
    setIsDirty(true);
    setSaveError(null);
  };

  // carrier === null: « Tous les transporteurs » (clears the scope).
  const handleCarrierScopeChange = (status, carrier, checked) => {
    const current = form.carrierScopes[status] || [];
    let next = [];
    if (carrier !== null) {
      next = checked ? [...current.filter((c) => c !== carrier), carrier] : current.filter((c) => c !== carrier);
    }
    change('carrierScopes', status, next);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await axios.put('/api/logistics/status-instructions', buildStatusPayload(statuses, form), { headers: authHeaders() });
      applyEntries((response.data && response.data.statuses) || [], response.data && response.data.carrier_options);
      setIsDirty(false);
      setToast({ tone: 'success', text: t('statusInstructions.saved', 'Instructions de statut enregistrées.') });
    } catch (error) {
      console.error('Failed to save status instructions:', error);
      const detail = error.response && error.response.data && error.response.data.detail;
      setSaveError(typeof detail === 'string' ? detail : t('aiAgentsUi.status.saveError', 'L’enregistrement a échoué. Vos modifications sont conservées.'));
    } finally {
      setIsSaving(false);
    }
  };

  const discard = () => {
    setForm(statusFormFrom(statuses));
    setIsDirty(false);
    setSaveError(null);
  };

  const setFilter = (next) => {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      if (next === 'all') params.delete('filter'); else params.set('filter', next);
      return params;
    }, { replace: true });
  };

  // ---- derived ----------------------------------------------------------------

  const loading = statuses === null;
  const entries = useMemo(() => statuses || [], [statuses]);
  // Tiles and filters read the saved state: a row never vanishes while it is edited.
  const savedForm = useMemo(() => statusFormFrom(entries), [entries]);
  const saved = useMemo(() => statusSummary(entries, savedForm), [entries, savedForm]);
  const visible = entries.filter((entry) => matchesStatusFilter(entry, savedForm, filter));
  const groups = STATUS_FAMILY_ORDER
    .map((family) => ({ family, entries: visible.filter((entry) => statusFamily(entry.value) === family) }))
    .filter((group) => group.entries.length > 0);

  const familyLabels = {
    open: t('statusInstructions.groupOpen', 'Ouverts'),
    waiting: t('statusInstructions.groupWaiting', 'En attente'),
    processing: t('statusInstructions.groupProcessing', 'En traitement'),
    terminal: t('statusInstructions.groupTerminal', 'Terminés'),
    other: t('statusInstructions.groupOther', 'Autres'),
  };

  const filterLabels = {
    custom: t('aiAgentsUi.status.kpi.custom', 'Personnalisés'),
    excluded: t('aiAgentsUi.status.kpi.excluded', 'Jamais posés par l’IA'),
    inactive: t('aiAgentsUi.status.kpi.inactive', 'IA à l’arrêt'),
  };

  // ---- zones ------------------------------------------------------------------

  const renderKpis = () => {
    if (loading) {
      return (
        <div className="u-kpi-row si-v2__kpis" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton si-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!entries.length) return null;
    return (
      <div className="u-kpi-row si-v2__kpis">
        <Kpi
          icon={ListChecks}
          label={t('aiAgentsUi.status.kpi.statuses', 'Statuts')}
          value={formatCount(saved.total)}
          context={t('aiAgentsUi.status.kpi.settable', '{{count}} que l’IA peut choisir', { count: saved.aiSettable })}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <Kpi
          icon={PenLine}
          label={filterLabels.custom}
          value={formatCount(saved.custom)}
          unit={`/ ${formatCount(saved.total)}`}
          context={t('aiAgentsUi.status.kpi.customContext', 'définition de votre compte')}
          meter={{ value: saved.custom, max: saved.total, label: filterLabels.custom }}
          active={filter === 'custom'}
          onClick={() => setFilter('custom')}
        />
        <Kpi
          icon={Ban}
          tone={saved.excluded ? 'warning' : 'neutral'}
          label={filterLabels.excluded}
          value={formatCount(saved.excluded)}
          context={t('aiAgentsUi.status.kpi.excludedContext', 'votre équipe seule les pose')}
          active={filter === 'excluded'}
          onClick={() => setFilter('excluded')}
        />
        <Kpi
          icon={CirclePause}
          tone="neutral"
          label={filterLabels.inactive}
          value={formatCount(saved.inactive)}
          context={t('aiAgentsUi.status.kpi.inactiveContext', 'dont {{count}} résolus, toujours', { count: saved.inactiveLocked })}
          active={filter === 'inactive'}
          onClick={() => setFilter('inactive')}
        />
      </div>
    );
  };

  const renderRow = (entry) => {
    const isOpen = Boolean(expanded[entry.value]);
    const override = form.instructions[entry.value] || '';
    const flags = statusFlags(entry, form);
    const changed = (entry.custom_instruction || '') !== override
      || (entry.ai_disabled === true) !== Boolean(form.aiDisabled[entry.value])
      || (entry.ai_inactive === true) !== Boolean(form.aiInactive[entry.value])
      || [...(entry.carrier_scope || [])].sort().join('|') !== [...(form.carrierScopes[entry.value] || [])].sort().join('|');
    const preview = flags.custom ? firstLine(override) : firstLine(entry.default_definition);
    const bodyId = `si-body-${entry.value}`;
    return (
      <li key={entry.value} className={`si-v2__row${isOpen ? ' is-open' : ''}`}>
        <button
          type="button"
          className="si-v2__head"
          aria-expanded={isOpen}
          aria-controls={bodyId}
          onClick={() => setExpanded((previous) => ({ ...previous, [entry.value]: !previous[entry.value] }))}
        >
          <ChevronRight className="si-v2__caret" aria-hidden="true" />
          <span className="si-v2__label">{getStatusLabel(t, entry.value)}</span>
          <span className="si-v2__badges">
            {flags.custom
              ? <Badge tone="accent">{t('statusInstructions.customChip', 'Personnalisé')}</Badge>
              : <Badge>{t('statusInstructions.defaultChip', 'Par défaut')}</Badge>}
            {flags.excluded && <Badge tone="warning" dot>{t('statusInstructions.excludedChip', 'Jamais par l’IA')}</Badge>}
            {flags.inactive && <Badge dot>{t('statusInstructions.inactiveChip', 'IA à l’arrêt')}</Badge>}
            {flags.scope.length > 0 && (
              <Badge>{t('statusInstructions.carrierScopeChip', 'Seulement : {{carriers}}', { carriers: flags.scope.join(', ') })}</Badge>
            )}
            {changed && <Badge tone="info">{t('aiAgentsUi.status.changed', 'Modifié')}</Badge>}
          </span>
          {!isOpen && <span className={`si-v2__preview${flags.custom ? '' : ' is-default'}`} title={preview}>{preview}</span>}
        </button>
        {isOpen && (
          <div className="si-v2__body" id={bodyId}>
            <div className="si-v2__switches">
              {entry.ai_settable === true && (
                <div className="si-v2__switch">
                  <Switch
                    id={`si-ai-disabled-${entry.value}`}
                    checked={flags.excluded}
                    onChange={(checked) => change('aiDisabled', entry.value, checked)}
                    label={t('statusInstructions.aiNeverSets', 'L’IA ne pose jamais ce statut')}
                  />
                  <p className="u-help">{t('statusInstructions.aiNeverSetsHint', 'L’IA ne le choisit plus sur ce compte ; votre équipe peut toujours le poser à la main.')}</p>
                </div>
              )}
              <div className="si-v2__switch">
                <Switch
                  id={`si-ai-inactive-${entry.value}`}
                  checked={flags.inactive}
                  disabled={entry.ai_inactive_locked === true}
                  onChange={(checked) => change('aiInactive', entry.value, checked)}
                  label={t('statusInstructions.aiInactive', 'L’IA ne fait plus rien sur ce statut')}
                />
                <p className="u-help">
                  {entry.ai_inactive_locked === true
                    ? t('statusInstructions.aiInactiveLocked', 'Toujours le cas pour un ticket résolu.')
                    : t('statusInstructions.aiInactiveHint', 'Ni réponse, ni relance du transporteur, ni réveil tant que le ticket a ce statut ; votre équipe garde la main.')}
                </p>
              </div>
            </div>
            {entry.ai_settable === true && !flags.excluded && (
              <fieldset className="si-v2__scope">
                <legend className="u-label">
                  {t('statusInstructions.carrierScopeLabel', 'Transporteurs pour lesquels l’IA utilise ce statut')}
                </legend>
                <div className="si-v2__scope-options">
                  <label className="si-v2__scope-option">
                    <input
                      type="checkbox"
                      checked={flags.scope.length === 0}
                      onChange={() => handleCarrierScopeChange(entry.value, null, true)}
                    />
                    {t('statusInstructions.carrierScopeAll', 'Tous les transporteurs')}
                  </label>
                  {carrierOptions.map((carrier) => (
                    <label key={carrier} className="si-v2__scope-option">
                      <input
                        type="checkbox"
                        checked={flags.scope.includes(carrier)}
                        onChange={(event) => handleCarrierScopeChange(entry.value, carrier, event.target.checked)}
                      />
                      {carrier}
                    </label>
                  ))}
                </div>
                <p className="u-help">
                  {carrierOptions.length === 0
                    ? t('statusInstructions.carrierScopeNone', 'Aucun transporteur configuré sur ce compte : ajoutez-les dans la page Transporteurs.')
                    : t('statusInstructions.carrierScopeHint', 'Par défaut, tous. Si vous cochez des transporteurs, l’IA ne pose, ne garde ni ne propose ce statut que sur leurs tickets ; votre équipe peut toujours le poser. Chaque transporteur compte à part : « DPD » n’inclut pas « DPD B2B ».')}
                </p>
              </fieldset>
            )}
            {entry.ai_settable === false && (
              <p className="si-v2__fixed">
                {t('statusInstructions.aiNeverSetsFixed', 'Jamais posé par l’IA : ce statut vient de la création du ticket ou d’une assignation à un membre de l’équipe.')}
              </p>
            )}
            <div className="si-v2__default">
              <span className="si-v2__default-head">
                <span className="u-overline">{t('statusInstructions.defaultDefinition', 'Définition par défaut — envoyée à l’IA')}</span>
                <code className="u-code">{entry.value}</code>
              </span>
              <p>{entry.default_definition}</p>
            </div>
            <Field
              label={t('statusInstructions.overrideLabel', 'Instruction personnalisée')}
              htmlFor={`si-override-${entry.value}`}
              help={t('aiAgentsUi.status.overrideHelp', 'Remplace la définition par défaut pour ce statut, sur votre compte.')}
            >
              <textarea
                id={`si-override-${entry.value}`}
                className="u-textarea si-v2__textarea"
                placeholder={t('statusInstructions.overridePlaceholder', 'Optionnel : remplace la définition par défaut pour ce statut.')}
                value={override}
                onChange={(event) => change('instructions', entry.value, event.target.value)}
              />
            </Field>
            {flags.custom && (
              <div>
                <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => change('instructions', entry.value, '')}>
                  <RotateCcw aria-hidden="true" />
                  {t('aiAgentsUi.status.clearOverride', 'Revenir à la définition par défaut')}
                </button>
              </div>
            )}
          </div>
        )}
      </li>
    );
  };

  const saveButton = (
    <button type="button" className="u-btn u-btn--primary" onClick={handleSave} disabled={isSaving || !isDirty}>
      <Save aria-hidden="true" />
      {isSaving ? t('statusInstructions.saving', 'Enregistrement…') : t('statusInstructions.save', 'Enregistrer')}
    </button>
  );

  return (
    <div className="u-page si-v2">
      <PageHeader
        title={t('statusInstructions.title', 'Instructions de statut')}
        subtitle={loading
          ? t('aiAgentsUi.status.subtitleShort', 'L’agent IA choisit lui-même le statut d’un ticket à partir de ces définitions.')
          : t('aiAgentsUi.status.subtitle', 'L’agent IA choisit lui-même le statut d’un ticket à partir de ces définitions · {{custom}} personnalisés sur {{total}}', { custom: saved.custom, total: saved.total })}
        actions={(
          <>
            {isDirty && !isSaving && <Badge tone="info" dot>{t('statusInstructions.unsaved', 'Modifications non enregistrées')}</Badge>}
            {isDirty && (
              <button type="button" className="u-btn u-btn--secondary" onClick={discard} disabled={isSaving}>
                {t('aiAgentsUi.status.discard', 'Annuler les modifications')}
              </button>
            )}
            {saveButton}
          </>
        )}
      />

      <div className="u-well">
        {loadError && (
          <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={fetchData}>{t('aiAgentsUi.retry', 'Réessayer')}</button>}>
            {loadError}
          </Banner>
        )}
        {saveError && <Banner tone="danger">{saveError}</Banner>}
        {renderKpis()}

        {filter !== 'all' && !loading && (
          <div className="si-v2__filter">
            <Badge tone="accent">{filterLabels[filter]}</Badge>
            <span className="u-muted">{t('aiAgentsUi.status.filterCount', '{{count}} statuts affichés', { count: visible.length })}</span>
            <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setFilter('all')}>
              <X aria-hidden="true" />
              {t('aiAgentsUi.clearFilter', 'Retirer le filtre')}
            </button>
          </div>
        )}

        {loading ? (
          <span className="u-skeleton si-v2__card-skeleton" aria-hidden="true" />
        ) : !visible.length ? (
          <Card>
            <p className="u-muted si-v2__none">
              {filter === 'all'
                ? t('aiAgentsUi.status.none', 'Aucun statut à afficher.')
                : t('aiAgentsUi.status.noneFiltered', 'Aucun statut dans ce filtre.')}
            </p>
          </Card>
        ) : groups.map((group) => (
          <Card
            key={group.family}
            title={(
              <span className="si-v2__group-title">
                <span className={`si-v2__dot si-v2__dot--${group.family}`} aria-hidden="true" />
                {familyLabels[group.family]}
              </span>
            )}
            badge={<Badge>{group.entries.length}</Badge>}
            flush
          >
            <ul className="si-v2__list">{group.entries.map(renderRow)}</ul>
          </Card>
        ))}

        {isDirty && (
          <div className="si-v2__savebar" role="region" aria-label={t('statusInstructions.unsaved', 'Modifications non enregistrées')}>
            <span>{t('statusInstructions.unsaved', 'Modifications non enregistrées')}</span>
            <span className="u-toolbar__spacer" />
            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={discard} disabled={isSaving}>
              {t('aiAgentsUi.status.discard', 'Annuler les modifications')}
            </button>
            <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? t('statusInstructions.saving', 'Enregistrement…') : t('statusInstructions.save', 'Enregistrer')}
            </button>
          </div>
        )}
      </div>

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default StatusInstructions;
